// components/ReportGenerator.js
import React, { useState } from 'react';
import { X, FileText, Mail, Download } from 'lucide-react';
import { formatKoreanDate, formatKoreanTimeUserFriendly } from '../utils/timeUtils';
import { getCustomerStages } from '../utils/customerStages';

const ReportGenerator = ({ customers, onClose, businessType = 'insurance' }) => {
    const CUSTOMER_STAGES = getCustomerStages(businessType);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [reportTitle, setReportTitle] = useState('고객 현황 보고서');
    const [isGenerating, setIsGenerating] = useState(false);

    // 고객 통계 계산
    const calculateStats = () => {
        const stats = {};
        let totalCustomers = 0;
        let totalValue = 0;

        CUSTOMER_STAGES.forEach(stage => {
            const stageCustomers = customers[stage.id] || [];
            stats[stage.id] = {
                count: stageCustomers.length,
                customers: stageCustomers,
                totalValue: stageCustomers.reduce((sum, customer) => sum + (customer.value || 0), 0)
            };
            totalCustomers += stageCustomers.length;
            totalValue += stats[stage.id].totalValue;
        });

        return { stats, totalCustomers, totalValue };
    };

    // 워드 문서 생성 (HTML 기반)
    const generateWordDocument = () => {
        const { stats, totalCustomers, totalValue } = calculateStats();
        const today = formatKoreanDate(new Date());

        const formatMoney = (amount) => {
            if (amount >= 100000000) {
                return Math.round(amount / 100000000) + '억원';
            } else if (amount >= 10000) {
                return Math.round(amount / 10000) + '만원';
            } else if (amount > 0) {
                return amount.toLocaleString() + '원';
            }
            return '0원';
        };

        let htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>${reportTitle}</title>
    <style>
        body { 
            font-family: 'Malgun Gothic', Arial, sans-serif; 
            margin: 40px; 
            line-height: 1.6;
            font-size: 11pt;
        }
        .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .title { 
            font-size: 18pt; 
            font-weight: bold; 
            margin-bottom: 10px; 
        }
        .date { 
            font-size: 10pt; 
            color: #666; 
        }
        .summary {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 25px;
        }
        .summary-item {
            display: inline-block;
            margin-right: 30px;
            margin-bottom: 8px;
        }
        .stage-section {
            margin-bottom: 25px;
            page-break-inside: avoid;
        }
        .stage-title {
            font-size: 14pt;
            font-weight: bold;
            color: #333;
            border-left: 4px solid #007bff;
            padding-left: 10px;
            margin-bottom: 15px;
        }
        .customer-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            font-size: 10pt;
        }
        .customer-table th, .customer-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
        }
        .customer-table th {
            background-color: #f1f3f4;
            font-weight: bold;
        }
        .stage-stats {
            font-size: 10pt;
            color: #666;
            margin-bottom: 10px;
        }
        .footer {
            margin-top: 40px;
            border-top: 1px solid #ccc;
            padding-top: 15px;
            font-size: 9pt;
            color: #666;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">${reportTitle}</div>
        <div class="date">작성일: ${today}</div>
    </div>

    <div class="summary">
        <h3>전체 현황 요약</h3>
        <div class="summary-item"><strong>총 고객 수:</strong> ${totalCustomers}명</div>
        <div class="summary-item"><strong>총 예상 계약액:</strong> ${formatMoney(totalValue)}</div>
        <div class="summary-item"><strong>보고서 생성:</strong> FC퍼널 시스템</div>
    </div>
`;

        // 각 단계별 상세 정보
        CUSTOMER_STAGES.forEach(stage => {
            const stageData = stats[stage.id];
            htmlContent += `
    <div class="stage-section">
        <div class="stage-title">${stage.emoji} ${stage.title}</div>
        <div class="stage-stats">
            고객 수: ${stageData.count}명 | 
            예상 계약액: ${formatMoney(stageData.totalValue)} | 
            ${stage.subtitle}
        </div>
`;

            if (stageData.customers.length > 0) {
                htmlContent += `
        <table class="customer-table">
            <thead>
                <tr>
                    <th>고객명</th>
                    <th>연락처</th>
                    <th>회사</th>
                    <th>예상 계약액</th>
                    <th>우선순위</th>
                    <th>메모</th>
                </tr>
            </thead>
            <tbody>
`;
                stageData.customers.forEach(customer => {
                    const priorityText = customer.priority === 'high' ? '높음' :
                        customer.priority === 'medium' ? '보통' : '낮음';
                    htmlContent += `
                <tr>
                    <td>${customer.name}</td>
                    <td>${customer.phone}</td>
                    <td>${customer.company || '-'}</td>
                    <td>${formatMoney(customer.value || 0)}</td>
                    <td>${priorityText}</td>
                    <td>${customer.memo || '-'}</td>
                </tr>
`;
                });
                htmlContent += `
            </tbody>
        </table>
`;
            } else {
                htmlContent += `<p style="color: #999; font-style: italic;">해당 단계에 고객이 없습니다.</p>`;
            }
            htmlContent += `</div>`;
        });

        htmlContent += `
    <div class="footer">
        본 보고서는 FC퍼널 고객관리 시스템에서 자동 생성되었습니다.<br>
        문의: 010-9705-0511 | 생성 시각: ${formatKoreanTimeUserFriendly(new Date())}
    </div>
</body>
</html>`;

        return htmlContent;
    };

    // 워드 파일 다운로드
    const downloadWordFile = () => {
        setIsGenerating(true);

        try {
            const htmlContent = generateWordDocument();
            const blob = new Blob([htmlContent], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${reportTitle}_${new Date().toISOString().slice(0, 10)}.doc`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            alert('보고서가 다운로드되었습니다.');
        } catch (error) {
            console.error('파일 생성 오류:', error);
            alert('파일 생성 중 오류가 발생했습니다.');
        } finally {
            setIsGenerating(false);
        }
    };

    // 이메일로 보내기
    const sendByEmail = () => {
        if (!recipientEmail) {
            alert('받는 사람 이메일을 입력해주세요.');
            return;
        }

        const { stats, totalCustomers, totalValue } = calculateStats();

        const formatMoney = (amount) => {
            if (amount >= 100000000) {
                return Math.round(amount / 100000000) + '억원';
            } else if (amount >= 10000) {
                return Math.round(amount / 10000) + '만원';
            } else if (amount > 0) {
                return amount.toLocaleString() + '원';
            }
            return '0원';
        };

        // 이메일 본문 생성 (개인정보 제거)
        let emailBody = `${reportTitle}

작성일: ${new Date().toLocaleDateString('ko-KR')}

=== 전체 현황 요약 ===
• 총 고객 수: ${totalCustomers}명
• 총 예상 계약액: ${formatMoney(totalValue)}

=== 단계별 현황 ===
`;

        CUSTOMER_STAGES.forEach(stage => {
            const stageData = stats[stage.id];
            emailBody += `
${stage.emoji} ${stage.title} (${stageData.count}명, ${formatMoney(stageData.totalValue)})
`;
            if (stageData.customers.length > 0) {
                stageData.customers.forEach(customer => {
                    // 이메일에서는 이름과 메모만 포함
                    const memo = customer.memo ? ` - ${customer.memo}` : '';
                    emailBody += `  - ${customer.name}${memo}
`;
                });
            }
        });

        emailBody += `

---
본 보고서는 FC퍼널 고객관리 시스템에서 생성되었습니다.
생성 시각: ${new Date().toLocaleString('ko-KR')}`;

        // mailto 링크 생성
        const subject = encodeURIComponent(reportTitle);
        const body = encodeURIComponent(emailBody);
        const mailtoLink = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;

        window.location.href = mailtoLink;
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full shadow-2xl border border-white/20">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                                <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">보고서 생성</h2>
                                <p className="text-gray-600 text-sm">고객 현황을 워드 문서로 생성</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-300"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            보고서 제목
                        </label>
                        <input
                            type="text"
                            value={reportTitle}
                            onChange={(e) => setReportTitle(e.target.value)}
                            className="w-full p-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                            placeholder="보고서 제목을 입력하세요"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            받는 사람 이메일 (선택사항)
                        </label>
                        <input
                            type="email"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            className="w-full p-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                            placeholder="manager@company.com"
                        />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl">
                        <h4 className="font-bold text-gray-800 mb-2">보고서 미리보기</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                            {CUSTOMER_STAGES.map(stage => {
                                const count = customers[stage.id]?.length || 0;
                                return (
                                    <div key={stage.id}>
                                        {stage.emoji} {stage.title}: {count}명
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={downloadWordFile}
                            disabled={isGenerating}
                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 flex items-center justify-center gap-2 shadow-lg shadow-green-500/25 transition-all duration-300 disabled:opacity-50"
                        >
                            <Download className="w-5 h-5" />
                            {isGenerating ? '생성 중...' : '다운로드'}
                        </button>
                        <button
                            onClick={sendByEmail}
                            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 transition-all duration-300"
                        >
                            <Mail className="w-5 h-5" />
                            이메일 발송
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportGenerator;